/**
 * Utilidades de fecha para el plan de entrenamiento (Sprint 8 S8.3 / UX-007).
 *
 * El backend serializa `TrainingWeek.start_date` como ISO date (YYYY-MM-DD).
 * Estas utilidades convierten ese string en strings legibles para la UI sin
 * acoplar a `Intl.DateTimeFormat`, cuya salida en `es-ES` varía entre
 * implementaciones (algunos motores devuelven "may.", otros "may"). Aquí
 * hardcodeamos los meses cortos en español, decisión consciente: es una
 * tabla de 12 entradas estable que evita sorpresas cross-browser y nos
 * permite testear con seguridad.
 */

/** Meses cortos en español, ordenados por índice (0=Ene, 11=Dic).
 *  Mayúscula inicial sin punto final — formato corto canónico. */
export const MONTHS_SHORT_ES: readonly string[] = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

/** Parsea una ISO date `YYYY-MM-DD` como fecha local sin timezone shift.
 *  `new Date("2026-05-15")` interpreta como UTC y puede desplazar el día
 *  según la zona del usuario — esta función fuerza interpretación local.
 *  Valida también rangos de mes [1,12] y día [1,31] para rechazar
 *  strings sintácticamente válidos pero semánticamente imposibles
 *  (ej: "2026-13-99") que `new Date()` aceptaría con overflow silencioso. */
function _parseIsoDateLocal(iso: string): Date {
  const parts = iso.split("-");
  if (parts.length !== 3) return new Date(Number.NaN);
  const [y, m, d] = parts.map((s) => Number.parseInt(s, 10));
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d) ||
    m < 1 ||
    m > 12 ||
    d < 1 ||
    d > 31
  ) {
    return new Date(Number.NaN);
  }
  return new Date(y, m - 1, d);
}

/**
 * Devuelve el rango "DD-DD MMM" (mismo mes) o "DD MMM - DD MMM" (cruce
 * de mes) de una semana de 7 días que arranca en `startIso`.
 *
 * - Mismo mes: `"15-21 May"`.
 * - Cruce mes: `"27 May - 2 Jun"`.
 * - Cruce año: `"29 Dic - 4 Ene"` (sin marcar año — el plan completo se
 *   entiende como cercano; si en futuro necesitamos year, se añade aquí).
 *
 * Si `startIso` no es parseable, devuelve cadena vacía (defensa: el caller
 * no debería renderizar un dash inútil).
 */
export function formatWeekDateRange(startIso: string): string {
  const start = _parseIsoDateLocal(startIso);
  if (Number.isNaN(start.getTime())) return "";

  const end = new Date(start);
  end.setDate(start.getDate() + 6); // 7 días incluyendo start → +6 al last day

  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonthIdx = start.getMonth();
  const endMonthIdx = end.getMonth();

  if (startMonthIdx === endMonthIdx) {
    // Mismo mes: "15-21 May"
    return `${startDay}-${endDay} ${MONTHS_SHORT_ES[startMonthIdx]}`;
  }
  // Cruce de mes (puede ser también cruce de año):
  // "27 May - 2 Jun" o "29 Dic - 4 Ene".
  return (
    `${startDay} ${MONTHS_SHORT_ES[startMonthIdx]} - ` +
    `${endDay} ${MONTHS_SHORT_ES[endMonthIdx]}`
  );
}
